// REQ-P1-01c 消費実績1号: 認証エンドポイントの契約型付き呼び出し層。
//
// 画面側(JS)はこのモジュール経由で認証APIを呼ぶ。リクエスト/レスポンスの形は
// contracts/openapi.yaml が正本であり、契約が変わるとこのファイルの typecheck が
// 落ちることで「契約とクライアントの乖離」をCIが検出する。
//
// 返り値は既存画面の分岐(ok判定 + ボディ参照 + ステータス別文言)を壊さないよう
// { ok, status, data, response } の素直な形に揃えている。

import { api, type paths } from './client';

type AdminSessionResponse = NonNullable<
  paths['/session/admin']['get']['responses'][200]['content']
>['application/json'];
type ArtistSessionResponse = NonNullable<
  paths['/session/artist']['get']['responses'][200]['content']
>['application/json'];
type ArtistAuthSessionResponse = NonNullable<
  paths['/artist/auth/session']['get']['responses'][200]['content']
>['application/json'];
type AdminPasswordResetRequest = NonNullable<
  paths['/admin/forgot-password']['post']['requestBody']
>['content']['application/json'];
type AdminChangePasswordRequest = NonNullable<
  paths['/admin/change-password']['put']['requestBody']
>['content']['application/json'];
type AdminPasswordResetValidateResponse = NonNullable<
  paths['/admin/password-reset/{token}']['get']['responses'][200]['content']
>['application/json'];
type AdminPasswordResetConfirmRequest = NonNullable<
  paths['/admin/password-reset/{token}']['post']['requestBody']
>['content']['application/json'];
type AdminInviteResponse = NonNullable<
  paths['/admin/invites/{token}']['get']['responses'][200]['content']
>['application/json'];
type AdminInviteCompleteRequest = NonNullable<
  paths['/admin/invites/{token}/complete']['post']['requestBody']
>['content']['application/json'];
type AdminUsernameAvailableQuery = NonNullable<
  paths['/admin/username-available']['get']['parameters']['query']
>;
type AdminUsernameAvailableResponse = NonNullable<
  paths['/admin/username-available']['get']['responses'][200]['content']
>['application/json'];
type GoogleLoginRequest = NonNullable<
  paths['/auth/google/login']['post']['requestBody']
>['content']['application/json'];
type AdminGoogleLoginLinkRequest = NonNullable<
  paths['/auth/google/admin/link']['post']['requestBody']
>['content']['application/json'];
type ArtistGoogleLoginLinkRequest = NonNullable<
  paths['/auth/google/artist/link']['post']['requestBody']
>['content']['application/json'];

type CallResult<T> = {
  ok: boolean;
  status: number;
  data: T | undefined;
  response: Response;
};

function toResult<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): CallResult<T> {
  // openapi-fetch は非2xx時に error 側へパース済みボディを入れる。
  // 既存画面はエラーボディからも message を読むため data に統合して返す。
  const body = (result.data ?? result.error) as T | undefined;
  return {
    ok: result.response.ok,
    status: result.response.status,
    data: body,
    response: result.response,
  };
}

async function readResponseJson(response: Response): Promise<Record<string, unknown> | undefined> {
  try {
    const data = await response.clone().json();
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

export async function adminLogin(params: { username: string; password: string }) {
  const result = await api.POST('/admin-login', { body: params });
  return toResult(result);
}

export async function completeAdminLoginTwoFactor(params: {
  challengeId: string;
  code: string;
}) {
  const result = await api.POST('/admin-login/2fa', { body: params });
  return toResult(result);
}

export async function resendAdminLoginTwoFactor(params: { challengeId?: string }) {
  const result = await api.POST('/admin-login/2fa/resend', { body: params });
  return toResult(result);
}

export async function artistLogin(params: { email: string; password: string }) {
  const result = await api.POST('/artist-login', { body: params });
  return toResult(result);
}

export async function googleLogin(params: GoogleLoginRequest) {
  const result = await api.POST('/auth/google/login', { body: params });
  return toResult(result);
}

export async function getAdminGoogleLoginLinkStatus() {
  const result = await api.GET('/auth/google/admin/link');
  return toResult(result);
}

export async function linkAdminGoogleLogin(params: AdminGoogleLoginLinkRequest) {
  const result = await api.POST('/auth/google/admin/link', { body: params });
  return toResult(result);
}

export async function unlinkAdminGoogleLogin() {
  const result = await api.DELETE('/auth/google/admin/link');
  return toResult(result);
}

export async function getArtistGoogleLoginLinkStatus() {
  const result = await api.GET('/auth/google/artist/link');
  return toResult(result);
}

export async function linkArtistGoogleLogin(params: ArtistGoogleLoginLinkRequest) {
  const result = await api.POST('/auth/google/artist/link', { body: params });
  return toResult(result);
}

export async function unlinkArtistGoogleLogin() {
  const result = await api.DELETE('/auth/google/artist/link');
  return toResult(result);
}

export async function adminLogout() {
  const result = await api.POST('/admin-logout');
  return toResult(result);
}

export async function artistLogout() {
  const result = await api.POST('/artist-logout');
  return toResult(result);
}

export async function changeAdminPassword(params: AdminChangePasswordRequest) {
  const result = await api.PUT('/admin/change-password', { body: params });
  const parsedData = await readResponseJson(result.response);
  return {
    ok: result.response.ok,
    status: result.response.status,
    data: parsedData ?? ((result.data ?? result.error) as Record<string, unknown> | undefined),
    response: result.response,
  };
}

export async function requestAdminPasswordReset(params: AdminPasswordResetRequest) {
  const result = await api.POST('/admin/forgot-password', { body: params });
  return toResult(result);
}

export async function validateAdminPasswordResetToken(token: string) {
  const result = await api.GET('/admin/password-reset/{token}', {
    params: { path: { token } },
  });
  return toResult<AdminPasswordResetValidateResponse>(result);
}

export async function completeAdminPasswordReset(
  token: string,
  params: AdminPasswordResetConfirmRequest
) {
  const result = await api.POST('/admin/password-reset/{token}', {
    params: { path: { token } },
    body: params,
  });
  return toResult(result);
}

export async function getAdminInvite(token: string) {
  const result = await api.GET('/admin/invites/{token}', {
    params: { path: { token } },
  });
  return toResult<AdminInviteResponse>(result);
}

export async function completeAdminInvite(token: string, params: AdminInviteCompleteRequest) {
  const result = await api.POST('/admin/invites/{token}/complete', {
    params: { path: { token } },
    body: params,
  });
  return toResult(result);
}

export async function checkAdminUsernameAvailable(query: AdminUsernameAvailableQuery) {
  const result = await api.GET('/admin/username-available', {
    params: { query },
  });
  return toResult<AdminUsernameAvailableResponse>(result);
}

export async function getAdminSessionResult(): Promise<CallResult<AdminSessionResponse>> {
  const result = await api.GET('/session/admin');
  return toResult<AdminSessionResponse>(result);
}

export async function getAdminSession(): Promise<AdminSessionResponse | null> {
  const result = await getAdminSessionResult();
  if (!result.ok || !result.data) return null;
  return result.data as AdminSessionResponse;
}

export async function getArtistSessionResult(): Promise<CallResult<ArtistSessionResponse>> {
  const result = await api.GET('/session/artist');
  return toResult<ArtistSessionResponse>(result);
}

export async function getArtistSession(): Promise<ArtistSessionResponse | null> {
  const result = await getArtistSessionResult();
  if (!result.ok || !result.data) return null;
  return result.data as ArtistSessionResponse;
}

export async function getArtistAuthSession(): Promise<ArtistAuthSessionResponse | null> {
  const { data, response } = await api.GET('/artist/auth/session');
  if (!response.ok || !data) return null;
  return data as ArtistAuthSessionResponse;
}
